<div [class.ms-xlarge-b]="displayMargin()" [formGroup]="parentForm()">
  <div class="relative ps-xxsmall-b" [attr.data-testid]="controlName()">
    @if (!hideLabel()) {
      <label
        [for]="idOrControlName()"
        [ngClass]="{
          'nordic-blue-400-text': !control().invalid || !control().touched,
          'error-900-text': control().invalid && control().touched,
        }"
        class="font-body-1 regular-type flex ps-xxsmall-b font-body-medium text-icon-secondary"
      >
        <div class="flex-1">
          @if (label()) {
            {{ label() }}
          } @else {
            {{ labelPrefix() }}
            {{ translationPrefix() + '.label' | finLocalization }}
            {{ labelSuffix() }}
          }
          @if (showAsterisk()) {
            <span class="flex-1 flex--nogrow" data-testid="text-input-required-asterisk"> *</span>
          }
        </div>
        <ng-template #contentTooltip>
          <h2 class="font-body-1 bold-type nordic-blue-900-text text-icon-primary font-body-medium-bold">
            {{ tooltipHeading() }}
          </h2>
          <p class="pr-xxsmall-t nordic-blue-400-text text-icon-secondary">{{ tooltipContent() }}</p>
        </ng-template>

        @if (useCustomTooltipAction()) {
          <fcom-icon-button
            [dataTestId]="'text-input-' + controlName() + '-info-button'"
            [aria]="{ label: 'actions.info' | finLocalization }"
            [icon]="{ name: SvgLibraryIcon.HELP }"
            [size]="IconButtonSize.SMALL"
            [theme]="IconButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE"
            (clickBtn)="tooltipClicked.emit($event)"
            class="icon nordic-blue-900-text text-icon-primary ms-small-r flex-1 flex--nogrow"
          />
        } @else if (tooltipContent()) {
          <fcom-icon-button
            [dataTestId]="'text-input-' + controlName() + '-close-tooltip-button'"
            [aria]="{ label: 'actions.info' | finLocalization }"
            [ariaCloseText]="'actions.close'"
            [content]="contentTooltip"
            [icon]="{ name: SvgLibraryIcon.HELP }"
            [options]="tooltipOptions()"
            [size]="IconButtonSize.SMALL"
            [theme]="IconButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE"
            (clickBtn)="tooltipClicked.emit($event)"
            class="icon nordic-blue-900-text text-icon-primary ms-small-r flex-1 flex--nogrow"
            fcomPopover
          />
        }
      </label>
    }

    @if (type() === TextInputType.INPUT) {
      <input
        #inputElement
        data-testid="fcom-text-input"
        [attr.aria-activedescendant]="ariaOptions().activedescendant"
        [attr.aria-controls]="ariaOptions().controls"
        [attr.aria-describedby]="ariaOptions().describedby"
        [attr.aria-details]="ariaOptions().details"
        [attr.aria-expanded]="ariaOptions().expanded"
        [attr.aria-required]="isRequired()"
        [attr.autocomplete]="disableAutoComplete() ? 'off' : ''"
        [attr.placeholder]="
          placeHolderText() ? placeHolderText() : placeHolder() ? (placeHolder() | finLocalization) : null
        "
        [class.form-field-empty]="!(control().value || placeHolder() || placeHolderText())"
        [class.sensitive]="isSensitive()"
        [formControlName]="controlName()"
        [id]="idOrControlName()"
        [name]="controlName()"
        [ngClass]="{ 'ps-xxlarge-r': icon(), 'link-hover': cursorType() === CursorType.POINTER }"
        [type]="isPassword() && !isPasswordVisible() ? InputType.PASSWORD : inputType()"
        (blur)="onBlur($event)"
        (input)="onInput($event)"
        (paste)="onPaste()"
        (keydown.enter)="onEnter()"
        class="font-body-1 medium-type ps-small rounded-large fill white-bg surface-primary font-label-medium"
        [class.border]="themeService.siteTheme() === FinnairTheme.LEGACY"
        [class.border-formfields]="themeService.siteTheme() !== FinnairTheme.LEGACY"
      />
    }

    @if (isPassword()) {
      <fcom-icon-button
        [dataTestId]="'text-input-' + controlName() + '-toggle-password-button'"
        [aria]="{
          label: isPasswordVisible()
            ? ('actions.hidePassword' | finLocalization)
            : ('actions.showPassword' | finLocalization),
        }"
        [class.disabled]="control().disabled"
        [icon]="{
          name: isPasswordVisible() ? SvgLibraryIcon.VISIBLE : SvgLibraryIcon.NOT_VISIBLE,
        }"
        [theme]="IconButtonTheme.LIGHT_TERTIARY_FORM"
        (clickBtn)="handleIconClick()"
        class="absolute icon-alignment"
      />
    } @else if (clearInput()) {
      <fcom-icon-button
        [dataTestId]="
          'text-input-' + controlName() + '-' + (control().value ? 'delete' : 'search') + '-selection-button'
        "
        [aria]="{
          label: control().value ? ('actions.clear' | finLocalization) : ('actions.search' | finLocalization),
        }"
        [class.disabled]="control().disabled"
        [icon]="{
          name: control().value ? SvgLibraryIcon.CLOSE_DELETE : SvgLibraryIcon.SEARCH,
        }"
        [theme]="IconButtonTheme.LIGHT_TERTIARY_FORM"
        (clickBtn)="handleIconClick()"
        class="absolute icon-alignment"
      />
    }

    @if (control().disabled === false) {
      <span class="divider"></span>
    }

    @if (type() === TextInputType.TEXTAREA) {
      <textarea
        #textareaElement
        [attr.aria-activedescendant]="ariaOptions().activedescendant"
        [attr.aria-controls]="ariaOptions().controls"
        [attr.aria-describedby]="ariaOptions().describedby"
        [attr.aria-details]="ariaOptions().details"
        [attr.aria-expanded]="ariaOptions().expanded"
        [attr.aria-required]="isRequired()"
        [attr.autocomplete]="disableAutoComplete() ? 'off' : ''"
        [attr.placeholder]="placeHolder() ? (placeHolder() | finLocalization) : null"
        [class.resizeable]="isResizeable()"
        [class.sensitive]="isSensitive()"
        [formControlName]="controlName()"
        [id]="idOrControlName()"
        [rows]="rows()"
        (blur)="blurEvent.emit($event)"
        (input)="inputEvent.emit($event)"
        class="font-body-1 medium-type ps-small border rounded-large white-bg surface-primary fill font-label-medium"
        type="text"
      ></textarea>
    }
  </div>

  @if ((hideErrorLabel() || !control().invalid || !control().touched) && helpText()) {
    <aside
      class="font-body-2 font-body-small"
      data-testid="help-text"
      [innerHTML]="helpText() | finSafe: 'html'"
    ></aside>
  }

  @if (!hideErrorLabel() && control().invalid && control().touched) {
    <div
      [attr.data-testid]="'error-message-for-' + controlName()"
      aria-live="polite"
      class="error-label error-900-text signal-error-notification-border-icon-text font-body-2 font-body-small"
      role="alert"
    >
      @if (control().errors?.required) {
        <span>
          {{ (translationErrorPrefix() || translationPrefix()) + '.required' | finLocalization }}
        </span>
      }
      @if (control().errors?.email || control().errors?.pattern) {
        <span>
          {{ (translationErrorPrefix() || translationPrefix()) + '.pattern' | finLocalization }}
        </span>
      }
      @if (control().errors?.duplicate) {
        <span>
          {{ (translationErrorPrefix() || translationPrefix()) + '.duplicate' | finLocalization }}
        </span>
      }
      @if (control().errors?.maxlength) {
        <span>
          {{
            (translationErrorPrefix() || translationPrefix()) + '.maxlength'
              | finLocalization: control().errors?.maxlength
          }}
        </span>
      }
      @if (control().errors?.minlength) {
        <span>
          {{
            (translationErrorPrefix() || translationPrefix()) + '.minlength'
              | finLocalization: control().errors?.minlength
          }}
        </span>
      }
      @if (control().errors?.custom) {
        <span>
          {{ (translationErrorPrefix() || translationPrefix()) + '.' + control().errors.custom | finLocalization }}
        </span>
      }
    </div>
  }

  @if (characterLimit() && resolvedCharacterCount()) {
    <div
      [attr.data-testid]="'character-limit-for-' + controlName()"
      class="nordic-blue-900-text text-icon-primary font-body-1 font-body-medium"
    >
      {{ resolvedCharacterCount() }} / {{ characterLimit() }}
    </div>
  }
</div>
